import { coerceWhoWereReachingMemberCount } from './whoWereReachingSnapshotDetect.util';

/** Donut chart row for age or race/ethnicity in who-were-reaching snapshots. */
export interface WhoWereReachingDistributionSnapshotRow {
  label: string;
  value: number;
  pct: number;
}

/**
 * Sort key for warehouse `age_bucket` labels (e.g. `41-50`, `65+`).
 * @param label - Age bucket label from BigQuery.
 * @returns Numeric sort key (lower bound of range when parseable).
 */
export const ageBucketSortKey = (label: string): number => {
  const trimmed = label.trim();
  const leading = /^\s*(\d+)/.exec(trimmed);
  if (leading) {
    return Number.parseInt(leading[1], 10);
  }
  if (/^under\s*18/i.test(trimmed) || /^<=\s*17/i.test(trimmed)) {
    return 0;
  }
  return 9999;
};

/**
 * Normalizes distribution rows from snapshot JSON (coerces counts and pct).
 * @param raw - `age` or `raceEthnicity` array from snapshot.
 * @returns Valid distribution rows.
 */
export const coerceWhoWereReachingDistribution = (
  raw: unknown,
): WhoWereReachingDistributionSnapshotRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as {
        label?: unknown;
        value?: unknown;
        pct?: unknown;
        memberCount?: unknown;
      };
      const label = typeof record.label === 'string' ? record.label.trim() : '';
      const value = coerceWhoWereReachingMemberCount(
        record.value ?? record.memberCount,
      );
      const pct =
        typeof record.pct === 'number' && Number.isFinite(record.pct)
          ? Math.round(record.pct)
          : 0;
      if (!label || value <= 0) {
        return null;
      }
      return { label, value, pct };
    })
    .filter((row): row is WhoWereReachingDistributionSnapshotRow => row !== null);
};

/**
 * Coerces illustrative distribution rows that only store `pct` (mock snapshots).
 * @param raw - `age` or `raceEthnicity` array from mock section data.
 * @returns Distribution rows using `pct` as the member count proxy.
 */
export const coerceIllustrativeDistributionFromPctRows = (
  raw: unknown,
): WhoWereReachingDistributionSnapshotRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as {
        label?: unknown;
        pct?: unknown;
        value?: unknown;
      };
      const label = typeof record.label === 'string' ? record.label.trim() : '';
      const value = coerceWhoWereReachingMemberCount(
        record.value ?? record.pct,
      );
      const pct =
        typeof record.pct === 'number' && Number.isFinite(record.pct)
          ? Math.round(record.pct)
          : value;
      if (!label || value <= 0) {
        return null;
      }
      return { label, value, pct };
    })
    .filter((row): row is WhoWereReachingDistributionSnapshotRow => row !== null);
};

/**
 * @param rows - Label and count pairs from BigQuery aggregation.
 * @param compareLabels - Optional sort for display order.
 * @returns Distribution rows with member counts and rounded percentages.
 */
const buildDistributionFromLabelCountRows = (
  rows: Array<{ label: string; counts: unknown }>,
  compareLabels?: (
    a: WhoWereReachingDistributionSnapshotRow,
    b: WhoWereReachingDistributionSnapshotRow,
  ) => number,
): WhoWereReachingDistributionSnapshotRow[] => {
  const mapped = rows
    .map(row => ({
      label: String(row.label).trim(),
      value: coerceWhoWereReachingMemberCount(row.counts),
    }))
    .filter(row => row.label.length > 0 && row.value > 0);

  const total = mapped.reduce((sum, row) => sum + row.value, 0);
  if (total <= 0) {
    return [];
  }

  const distribution = mapped.map(row => ({
    label: row.label,
    value: row.value,
    pct: Math.round((row.value / total) * 100),
  }));

  if (compareLabels) {
    return distribution.sort(compareLabels);
  }
  return distribution;
};

/**
 * Builds age distribution donut rows from demographic warehouse age buckets.
 * @param rows - BigQuery rows grouped by `age_bucket`.
 * @returns Sorted distribution rows with member counts and rounded percentages.
 */
export const buildAgeDistributionFromBucketRows = (
  rows: Array<{ age_bucket: string; counts: unknown }>,
): WhoWereReachingDistributionSnapshotRow[] =>
  buildDistributionFromLabelCountRows(
    rows.map(row => ({ label: row.age_bucket, counts: row.counts })),
    (a, b) => ageBucketSortKey(a.label) - ageBucketSortKey(b.label),
  );

/**
 * Builds race/ethnicity donut rows from demographic warehouse `ethnicity_code` values.
 * @param rows - BigQuery rows grouped by `ethnicity_code`.
 * @returns Distribution rows sorted by member count descending.
 */
export const buildRaceEthnicityDistributionFromEthnicityRows = (
  rows: Array<{ ethnicity_code: string; counts: unknown }>,
): WhoWereReachingDistributionSnapshotRow[] =>
  buildDistributionFromLabelCountRows(
    rows.map(row => ({ label: row.ethnicity_code, counts: row.counts })),
    (a, b) => b.value - a.value || a.label.localeCompare(b.label),
  );

